/**
 * Identidad institucional del website AGDYSA.
 * Equivale a Identidad → Marca de SIAC, adaptada a las dos marcas del sitio
 * (AGDYSA + LABSYSO). Sin locución/TV: eso no aplica aquí.
 */

const emptyLogo = () => ({
  url: "",
  margenTopPx: 0,
  margenBottomPx: 0,
  margenLeftPx: 0,
  margenRightPx: 0,
  fondoVisible: false,
  fondoColor: "#FFFFFF",
  invertir: false,
});

export const LOGO_FIELDS = [
  { key: "agdysaHorizontal", label: "AGDYSA · imagotipo horizontal", hint: "Navbar y cabeceras" },
  { key: "agdysaIsotipo", label: "AGDYSA · isotipo", hint: "Favicon / PWA / espacios chicos" },
  { key: "agdysaMonoClaro", label: "AGDYSA · monocromático claro", hint: "Sobre fondos oscuros" },
  { key: "agdysaMonoOscuro", label: "AGDYSA · monocromático oscuro", hint: "Sobre fondos claros" },
  { key: "labsysoHorizontal", label: "LABSYSO · imagotipo horizontal", hint: "Navbar en /labsyso" },
  { key: "labsysoIsotipo", label: "LABSYSO · isotipo", hint: "Espacios chicos LABSYSO" },
];

export const COLOR_FIELDS = [
  { key: "dark", label: "Verde oscuro", hint: "Textos, fondos profundos", cssVar: "--color-brand-dark", hex: "#253f13" },
  { key: "primary", label: "Primario", hint: "Verde oliva, botones", cssVar: "--color-brand-primary", hex: "#658b10" },
  { key: "mid", label: "Verde medio", hint: "Estados hover / apoyo", cssVar: "--color-brand-mid", hex: "#6db022" },
  { key: "accent", label: "Acento", hint: "CTAs, highlights", cssVar: "--color-brand-accent", hex: "#b0cb3f" },
  { key: "icon", label: "Iconografía", hint: "Iconos de marca", cssVar: "--color-brand-icon", hex: "#3C7C3C" },
  { key: "navy", label: "Azul marino", hint: "Secundario institucional", cssVar: "--color-brand-navy", hex: "#294361" },
  { key: "soft", label: "Verde suave", hint: "Fondos de tarjetas", cssVar: "--color-brand-soft", hex: "#f0f7e6" },
  { key: "labsysoPrimary", label: "LABSYSO primario", hint: "Azul oscuro LABSYSO", cssVar: "--color-labsyso-primary", hex: "#221d52" },
  { key: "labsysoAccent", label: "LABSYSO acento", hint: "Dorado LABSYSO", cssVar: "--color-labsyso-accent", hex: "#f5b419" },
];

const FONT_DEFAULTS = {
  principal: {
    family: "Montserrat",
    googleFontsUrl:
      "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400;0,600;0,700;0,900;1,600&display=swap",
  },
  secundaria: {
    family: "Inter",
    googleFontsUrl: "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap",
  },
};

export function identityDefaults(contentLogos = {}) {
  const agdysa = contentLogos.agdysa_logo || "";
  const labsyso = contentLogos.labsyso_logo || "";
  return {
    nombreOficial: "Asesoría en Gestión y Desarrollo, S.A. de C.V.",
    acronimo: "AGDYSA",
    eslogan: "Preservando la vida",
    logos: {
      agdysaHorizontal: { ...emptyLogo(), url: agdysa },
      agdysaIsotipo: { ...emptyLogo(), url: agdysa },
      agdysaMonoClaro: { ...emptyLogo(), url: agdysa },
      agdysaMonoOscuro: { ...emptyLogo(), url: agdysa },
      labsysoHorizontal: { ...emptyLogo(), url: labsyso },
      labsysoIsotipo: { ...emptyLogo(), url: labsyso },
    },
    colores: Object.fromEntries(
      COLOR_FIELDS.map((f) => [f.key, { hex: f.hex, pantone: "" }])
    ),
    tipografia: FONT_DEFAULTS,
    iconos: {},
    proteccion: {
      margenMinPct: 20,
      restricciones: [
        "No distorsionar ni rotar los logos.",
        "Respetar el espacio de respeto (margen mínimo).",
        "No alterar los colores institucionales fuera de las variantes oficiales.",
      ],
    },
  };
}

function mergeLogo(raw, fallback) {
  const base = { ...emptyLogo(), ...(fallback || {}) };
  if (!raw || typeof raw !== "object") return base;
  return {
    ...base,
    ...raw,
    url: String(raw.url || base.url || "").trim(),
    margenTopPx: Number(raw.margenTopPx) || 0,
    margenBottomPx: Number(raw.margenBottomPx) || 0,
    margenLeftPx: Number(raw.margenLeftPx) || 0,
    margenRightPx: Number(raw.margenRightPx) || 0,
    fondoVisible: !!raw.fondoVisible,
    fondoColor: raw.fondoColor || "#FFFFFF",
    invertir: !!raw.invertir,
  };
}

export function isValidHex(v) {
  return /^#([0-9a-f]{6})$/i.test(String(v || "").trim());
}

export function mergeIdentity(raw, contentLogos = {}) {
  const d = identityDefaults(contentLogos);
  const src = raw && typeof raw === "object" ? raw : {};
  const logos = {};
  for (const { key } of LOGO_FIELDS) {
    logos[key] = mergeLogo(src.logos?.[key], d.logos[key]);
  }
  const colores = {};
  for (const f of COLOR_FIELDS) {
    const hex = src.colores?.[f.key]?.hex || d.colores[f.key].hex;
    colores[f.key] = {
      hex: isValidHex(hex) ? hex.trim() : d.colores[f.key].hex,
      pantone: src.colores?.[f.key]?.pantone || "",
    };
  }
  const iconos = {};
  if (src.iconos && typeof src.iconos === "object" && !Array.isArray(src.iconos)) {
    for (const [k, v] of Object.entries(src.iconos)) {
      if (String(k).trim() && String(v || "").trim()) iconos[String(k).trim()] = String(v).trim();
    }
  }
  return {
    nombreOficial: String(src.nombreOficial || d.nombreOficial).trim() || d.nombreOficial,
    acronimo: String(src.acronimo || d.acronimo).trim() || d.acronimo,
    eslogan: String(src.eslogan ?? d.eslogan).trim(),
    logos,
    colores,
    tipografia: {
      principal: {
        family: src.tipografia?.principal?.family || d.tipografia.principal.family,
        googleFontsUrl: src.tipografia?.principal?.googleFontsUrl || d.tipografia.principal.googleFontsUrl,
      },
      secundaria: {
        family: src.tipografia?.secundaria?.family || d.tipografia.secundaria.family,
        googleFontsUrl: src.tipografia?.secundaria?.googleFontsUrl || d.tipografia.secundaria.googleFontsUrl,
      },
    },
    iconos,
    proteccion: {
      margenMinPct: Number(src.proteccion?.margenMinPct ?? d.proteccion.margenMinPct) || 0,
      restricciones: Array.isArray(src.proteccion?.restricciones)
        ? src.proteccion.restricciones.map((s) => String(s).trim()).filter(Boolean)
        : d.proteccion.restricciones,
    },
  };
}

export function logoUrl(asset, fallback = "") {
  const url = typeof asset === "string" ? asset : asset?.url;
  return String(url || fallback || "").trim();
}

export function logoImgStyle(asset) {
  if (!asset || typeof asset !== "object") return undefined;
  return {
    paddingTop: asset.margenTopPx || 0,
    paddingBottom: asset.margenBottomPx || 0,
    paddingLeft: asset.margenLeftPx || 0,
    paddingRight: asset.margenRightPx || 0,
    filter: asset.invertir ? "invert(1)" : undefined,
  };
}

export function identityToSave(form, iconRows, restriccionesText) {
  const iconos = {};
  for (const row of iconRows || []) {
    const k = String(row.clave || "").trim();
    const url = String(row.url || "").trim();
    if (k && url) iconos[k] = url;
  }
  const merged = mergeIdentity({
    ...form,
    iconos,
    proteccion: {
      ...form.proteccion,
      restricciones: String(restriccionesText || "")
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
    },
  });
  return merged;
}
